import { memo, useRef, useState } from "react";
import { TaskStatus } from "@common/types/task";
import { TaskColumn } from "@common/types/views/task-view-enums";
import { taskFieldProps } from "@components/tasks/board/TaskChipButton";
import type { TaskCardEditField } from "@components/tasks/board/taskCardEdits";
import { withStatusField } from "@components/tasks/board/taskBoardFields";
import { getTaskPresenceMembers } from "@components/tasks/board/taskPresenceMembers";
import { TaskTitleEditor } from "@components/tasks/board/TaskTitleEditor";
import {
    type TaskActionItem,
    TaskActionMenuItems,
} from "@components/tasks/useTaskActions";
import { BoardCard } from "@design-system/Board/BoardCard";
import { BoardCardLayout } from "@design-system/Board/BoardCardLayout";
import { BoardCardMenu } from "@design-system/Board/BoardCardMenu";
import { BoardCardTitleButton } from "@design-system/Board/BoardCardTitleButton";
import { Menu } from "@design-system/Menu/Menu.lib";
import type { AgentDrawerMode } from "./boardPlaygroundSettings";
import { getDemoViewers, toDemoUser } from "./demoPresence";
import { getDemoTaskCardFields } from "./demoTaskCardFields";
import type { DemoEntity, DemoPerson, DemoTask } from "./fixtures/demoTasks";
import type { TaskBoardChange, TaskBoardField } from "./taskBoardActions";
import type { TaskCardFieldPlacements } from "./taskBoardCardFields";
import { TaskBoardActivity } from "./TaskBoardActivity";
import {
    getEditableTaskCardFields,
    TaskPresenceChip,
    TaskStatusChip,
} from "./TaskBoardEditableChips";
import chipStyles from "@components/tasks/board/TaskBoardChips.module.scss";

const NO_HIDDEN_FIELDS: ReadonlySet<string> = new Set();

interface TaskBoardCardProps {
    task: DemoTask;
    assignee: DemoPerson | undefined;
    createdBy: DemoPerson | undefined;
    entities: readonly DemoEntity[];
    /** Visible optional fields, by card field id. */
    fields: ReadonlySet<string>;
    /** The fields the columns and lanes already show, left off the card (`getTaskAxisFields`). */
    hiddenFields?: ReadonlySet<string>;
    placements: TaskCardFieldPlacements;
    /** Fields that edit from the card; the others read only. */
    editableFields: ReadonlySet<TaskBoardField>;
    /** The drawer while the agent has the task in progress. */
    agentDrawer?: AgentDrawerMode;
    onOpen: (task: DemoTask) => void;
    /** Applies a chip's edit to this task only. */
    onChange: (task: DemoTask, change: TaskBoardChange) => void;
}

/** The real board card's menu rows, as `useTaskActions` builds them; only Open, Open chat and Edit details act. */
const getMenuItems = (onOpen: () => void): TaskActionItem[] => [
    { kind: "item", id: "open", label: "Open", onClick: onOpen },
    { kind: "item", id: "open-chat", label: "Open chat", onClick: onOpen },
    {
        kind: "item",
        id: "edit-details",
        label: "Edit details",
        onClick: onOpen,
    },
    {
        kind: "item",
        id: "duplicate",
        label: "Duplicate task",
        onClick: () => undefined,
    },
    { kind: "separator", id: "separator" },
    {
        kind: "item",
        id: "delete",
        label: "Delete task",
        destructive: true,
        onClick: () => undefined,
    },
];

const TaskBoardCardImpl = ({
    task,
    assignee,
    createdBy,
    entities,
    fields,
    hiddenFields = NO_HIDDEN_FIELDS,
    placements,
    editableFields,
    agentDrawer = "none",
    onOpen,
    onChange,
}: Readonly<TaskBoardCardProps>) => {
    const isAgentWorking =
        agentDrawer !== "none" &&
        task.status === TaskStatus.IN_PROGRESS &&
        task.inProgressBy === "agent";
    const [isRenaming, setIsRenaming] = useState(false);
    const [addingField, setAddingField] = useState<TaskCardEditField | null>(
        null,
    );
    const cardRef = useRef<HTMLDivElement>(null);
    const changeTask = (change: TaskBoardChange) => onChange(task, change);
    const openTask = () => onOpen(task);
    const getEditChange = (field: TaskBoardField) =>
        editableFields.has(field) ? changeTask : undefined;
    const menuItems = <TaskActionMenuItems items={getMenuItems(openTask)} />;
    const getTitle = () => {
        if (isRenaming) {
            return (
                <TaskTitleEditor
                    initialTitle={task.title}
                    onRename={(title) => changeTask({ field: "title", title })}
                    onEnd={() => setIsRenaming(false)}
                    onReturnFocus={() =>
                        cardRef.current?.focus({ preventScroll: true })
                    }
                />
            );
        }
        return editableFields.has("title") ? (
            <BoardCardTitleButton
                {...taskFieldProps("title")}
                onClick={() => setIsRenaming(true)}
            >
                {task.title}
            </BoardCardTitleButton>
        ) : (
            task.title
        );
    };
    const displayFields = withStatusField({
        fields: getDemoTaskCardFields({
            task,
            assignee,
            createdBy,
            entities,
            fields,
            hiddenFields,
            placements,
            onOpenTask: openTask,
        }),
        status: !hiddenFields.has(TaskColumn.STATUS) && (
            <TaskStatusChip task={task} onChange={getEditChange("status")} />
        ),
    });
    return (
        <Menu.Context
            trigger={
                <BoardCard
                    ref={cardRef}
                    onClick={openTask}
                    className={chipStyles.taskCard}
                    drawer={
                        isAgentWorking && (
                            <TaskBoardActivity mode={agentDrawer} />
                        )
                    }
                >
                    <BoardCardLayout
                        title={getTitle()}
                        menu={<BoardCardMenu>{menuItems}</BoardCardMenu>}
                        fields={getEditableTaskCardFields({
                            fields: [
                                ...displayFields,
                                {
                                    id: "presence",
                                    placement: "bottomEnd",
                                    content: (
                                        <TaskPresenceChip
                                            task={task}
                                            members={getTaskPresenceMembers({
                                                viewers: getDemoViewers(
                                                    task.id,
                                                ),
                                                assignee:
                                                    assignee === undefined
                                                        ? null
                                                        : toDemoUser(assignee),
                                                isAgentWorking,
                                            })}
                                            onChange={getEditChange("assignee")}
                                        />
                                    ),
                                },
                            ],
                            task,
                            entities,
                            editableFields,
                            onChange: changeTask,
                            adding: {
                                field: addingField,
                                onFieldChange: setAddingField,
                            },
                        })}
                    />
                </BoardCard>
            }
        >
            {menuItems}
        </Menu.Context>
    );
};

/**
 * Task card for the board playground, built from the real card's pieces:
 * the status pill first (unless the columns show statuses), the blocked
 * tag, the shown fields in List's order with the entities' context line on
 * its own row, the unread comments, the presence stack bottom right and the
 * Add field chip on hover. Each field in `editableFields` edits this task
 * only, through the playground's pickers; menus and clicks are stubs. An
 * agent on the task shows in the drawer under the card and first in the
 * stack. Memoized: every slot of the board re-renders while a card is
 * dragged, and the content rarely changes.
 */
export const TaskBoardCard = memo(TaskBoardCardImpl);
